import { type FC, useMemo, useRef } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { CompanyUserRole, Goal, goalToString } from "shared";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { toFormValues, useForm } from "../common/form/useForm";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { useCompany, useCompanyActions } from "../company/useCompany";
import { Button } from "../components/Button";
import { MultiInput } from "../components/MultiInput";
import { BrandColorsEditor } from "./BrandColorsEditor";

const ALLOWED_LOGO_TYPES = [
	"image/png",
	"image/jpeg",
	"image/svg+xml",
	"image/webp",
];
const MAX_LOGO_SIZE = 5 * 1024 * 1024; // 5MB

export const TabCompanyInfo: FC = () => {
	const company = useCompany();
	const emit = useAlertEmit();
	const logoInputRef = useRef<HTMLInputElement>(null);
	const initial = useMemo(
		() => ({
			...toFormValues(company),
			locations: company.locations,
			brand_colors: company.brand_colors ?? [],
		}),
		[company],
	);
	const {
		updateCompany,
		uploadLogo,
		extractColorsFromLogo,
		updateBrandColors,
		updateApprovalSetting,
	} = useCompanyActions();
	const isOwner = company.myRole === CompanyUserRole.OWNER;
	const form = useForm({
		initialValues: initial,
		onSubmit: async (values) => {
			const {
				id: _,
				users: __,
				invites: ___,
				requirePostApproval: ____,
				myRole: _____,
				...updatable
			} = values;

			await updateCompany.mutateAsync({
				...updatable,
				goal: values.goal as Goal,
				// this will skip the update on this field
				icps: undefined,
			});
		},
	});

	const brandColors = (form.values.brand_colors as string[]) ?? [];
	const savedBrandColors = company.brand_colors ?? [];
	const brandColorsDirty =
		brandColors.length !== savedBrandColors.length ||
		brandColors.some((color, i) => color !== savedBrandColors[i]);

	const locationsErrorsKeys = Object.keys(form.errors).filter(
		(key) => key.split(".")[0] === "locations",
	) as Array<keyof typeof form.errors>;

	const transformedErrorLocations = useMemo(
		() =>
			locationsErrorsKeys.map((idxKey) => {
				const [_, indexString] = idxKey.split(".");
				const index = Number(indexString);
				const error = form.errors[idxKey];
				return {
					index,
					error: Array.isArray(error) ? error[0] : error,
				};
			}),
		[locationsErrorsKeys, form],
	);

	const locationsErrors = useMemo(
		() =>
			(form.values?.locations ?? []).map((_, index) => {
				const e = transformedErrorLocations.find(
					(error) => error.index === index,
				);

				const hasError = e !== undefined;

				return hasError ? (e.error ?? null) : null;
			}),
		[transformedErrorLocations, form.values.locations],
	);

	const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
			emit({
				type: AlertType.ERROR,
				content: "Please upload a valid image file (PNG, JPG, SVG, or WebP).",
			});
			return;
		}

		if (file.size > MAX_LOGO_SIZE) {
			emit({
				type: AlertType.ERROR,
				content: "Logo file must be smaller than 5MB.",
			});
			return;
		}

		await uploadLogo.mutateAsync(file);
		e.target.value = "";
	};

	const handleExtractColors = async () => {
		const colors = await extractColorsFromLogo.mutateAsync();
		form.setFieldValue("brand_colors", colors);
	};

	return (
		<Form onSubmit={form.handleSubmit}>
			<Row>
				<Col md={4}>
					<ExtraForm.Group label="Business name:" className="mb-5">
						<ExtraForm.Control
							placeholder="Write the name of you business"
							{...form.getFieldProps("name")}
							{...form.getFieldState("name")}
						/>
					</ExtraForm.Group>
				</Col>
				<Col md={4}>
					<ExtraForm.Group label="Website" className="mb-5">
						<ExtraForm.Control
							placeholder="Write the address of you website"
							{...form.getFieldProps("website")}
							{...form.getFieldState("website")}
						/>
					</ExtraForm.Group>
				</Col>
				<Col md={4}>
					<ExtraForm.Group label="What is your goal?" className="mb-5">
						<ExtraForm.Select
							{...form.getFieldProps("goal")}
							{...form.getFieldState("goal")}
						>
							{Object.values(Goal).map((goal) => (
								<option key={goal} value={goal}>
									{goalToString[goal]}
								</option>
							))}
						</ExtraForm.Select>
					</ExtraForm.Group>
				</Col>
				<Col md={4}>
					<ExtraForm.Group label="Marketing Tone" className="mb-5">
						<ExtraForm.Control
							as="textarea"
							rows={2}
							{...form.getFieldProps("tone")}
							{...form.getFieldState("tone")}
							placeholder="Describe the marketing tone"
						/>
					</ExtraForm.Group>
				</Col>
				<Col md={4}>
					<ExtraForm.Group label="Marketing Voice" className="mb-5">
						<ExtraForm.Control
							as="textarea"
							rows={2}
							{...form.getFieldProps("voice")}
							{...form.getFieldState("voice")}
							placeholder="Describe the marketing voice"
						/>
					</ExtraForm.Group>
				</Col>
				<Col md={4}>
					<ExtraForm.Group label="Describe your business:" className="mb-3">
						<ExtraForm.Control
							as="textarea"
							rows={5}
							{...form.getFieldProps("description")}
							{...form.getFieldState("description")}
							placeholder="Describe your business...."
						/>
					</ExtraForm.Group>
				</Col>
				<Col md={4}>
					<ExtraForm.Group label="Locations">
						<MultiInput
							placeholder="Add a location"
							{...form.getFieldProps("locations")}
							{...form.getFieldState("locations")}
							onChange={(locations) =>
								form.setFieldValue("locations", locations)
							}
							error={locationsErrors}
						/>
					</ExtraForm.Group>
				</Col>

				{/* Brand Identity Section */}
				<Col md={12}>
					<h5 className="mb-3 mt-4">Brand Identity</h5>
				</Col>

				{/* Logo Upload */}
				<Col md={4}>
					<ExtraForm.Group label="Company Logo" className="mb-5">
						<input
							type="file"
							ref={logoInputRef}
							className="d-none"
							accept=".png,.jpg,.jpeg,.svg,.webp"
							onChange={handleLogoUpload}
						/>

						{company.logo_url ? (
							<div className="border rounded p-3 text-center bg-light">
								<img
									src={company.logo_url}
									alt={`${company.name} logo`}
									style={{
										maxWidth: "200px",
										maxHeight: "100px",
										objectFit: "contain",
									}}
								/>
								<div className="d-flex gap-2 justify-content-center mt-3">
									<Button
										size="sm"
										variant="ghost"
										onClick={() => logoInputRef.current?.click()}
										loading={uploadLogo.isPending}
									>
										Change Logo
									</Button>
									<Button
										size="sm"
										variant="ghost"
										onClick={handleExtractColors}
										loading={extractColorsFromLogo.isPending}
									>
										Extract Colors
									</Button>
								</div>
							</div>
						) : (
							<div className="border rounded p-3 text-center bg-light">
								<div className="text-muted mb-3">No logo uploaded</div>
								<Button
									size="sm"
									onClick={() => logoInputRef.current?.click()}
									loading={uploadLogo.isPending}
								>
									Upload Logo
								</Button>
							</div>
						)}
					</ExtraForm.Group>
				</Col>

				{/* Editable Color Palette */}
				<Col md={8}>
					<ExtraForm.Group label="Brand Colors" className="mb-5">
						<BrandColorsEditor
							colors={brandColors}
							onChange={(colors) => form.setFieldValue("brand_colors", colors)}
							onSave={() => updateBrandColors.mutate(brandColors)}
							saving={updateBrandColors.isPending}
							dirty={brandColorsDirty}
						/>
					</ExtraForm.Group>
				</Col>

				{/* Post Approval Settings - Only visible to owners */}
				{isOwner && (
					<>
						<Col md={12}>
							<h5 className="mb-3 mt-4">Team Settings</h5>
						</Col>
						<Col md={4}>
							<Form.Group className="mb-3">
								<Form.Check
									type="switch"
									label="Require post approvals by Client Accounts or Owners"
									id="settings-require-approval-switch"
									className="app-connect-switch"
									checked={company.requirePostApproval}
									onChange={(e) =>
										updateApprovalSetting.mutate(e.target.checked)
									}
									disabled={updateApprovalSetting.isPending}
								/>
								<Form.Text className="text-muted">
									When enabled, posts must be approved by a Client Account or
									Owner before they can be scheduled.
								</Form.Text>
							</Form.Group>
						</Col>
					</>
				)}
			</Row>
			<Row>
				<Col xs={4} md={2}>
					<Button loading={updateCompany.isPending} type="submit">
						Save
					</Button>
				</Col>
			</Row>
		</Form>
	);
};
